import React, { lazy } from "react";

import { reloadOnce } from "@/utils/chunkReload";

const CHUNK_ERROR_RE =
  /Loading chunk|Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module|'text\/html' is not a valid JavaScript MIME type|Unable to preload|Unexpected end of script|Unexpected EOF/i;

const isChunkError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error);
  return CHUNK_ERROR_RE.test(message);
};

// Brief visual fallback while the reload navigates away.
const ReloadNotice = () => (
  <div className="container">
    <p className="text">Memproses fitur, harap tunggu</p>
    <div className="loader" />
  </div>
);

export function lazyWithRetry<T>(
  componentImport: () => Promise<{ default: (props: T) => React.ReactNode }>
) {
  const isProd = import.meta.env.PROD;
  const reloadingModule: { default: (props: T) => React.ReactNode } = { default: ReloadNotice };

  return lazy(async () => {
    try {
      const loaded = await componentImport();

      // App.tsx cancels vite:preloadError while a reload is underway, and Vite then
      // resolves the import with undefined instead of throwing. React.lazy would read
      // undefined.default and crash into the ErrorBoundary.
      return loaded ?? reloadingModule;
    } catch (firstError) {
      // Only recover from chunk/preload errors. Real runtime bugs surface to Sentry.
      if (!isChunkError(firstError)) throw firstError;
      if (!isProd) throw firstError;

      // Retry once after a short delay — handles transient network blips.
      try {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return (await componentImport()) ?? reloadingModule;
      } catch (retryError) {
        // Recovery is rate limited; when it is suppressed let the error surface
        // to the ErrorBoundary and Sentry rather than failing silently.
        if (!reloadOnce()) throw retryError;

        return reloadingModule;
      }
    }
  });
}
